import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { useGlobalSettings, useUpdateGlobalSettings } from "../../hooks/use-api";
import {
  useInflationDefaults,
  useReferencePrefix,
  useUpdateInflationDefaults,
  useUpdateReferencePrefix,
} from "../../hooks/use-settings";

interface LocalOnlyDefaults {
  feesPercentage: number;
  demolitionPercentage: number;
  gstRate: number;
  sModal: number;
  eModal: number;
}

interface InflationDefaults {
  demoPlanMonths: number;
  constructionMonths: number;
  firstYearMonths: number;
  inflationYear1: number;
  inflationYear2: number;
  inflationYear3: number;
  inflationYear4: number;
}

const initialLocalOnlyDefaults: LocalOnlyDefaults = {
  feesPercentage: 13,
  demolitionPercentage: 8,
  gstRate: 15,
  sModal: 0,
  eModal: 0,
};

const initialInflationDefaults: InflationDefaults = {
  demoPlanMonths: 3,
  constructionMonths: 12,
  firstYearMonths: 12,
  inflationYear1: 3,
  inflationYear2: 3.5,
  inflationYear3: 4,
  inflationYear4: 4,
};

function Field({
  label,
  id,
  value,
  onChange,
}: {
  label: string;
  id: string;
  value: number;
  onChange: (v: number) => void;
}): React.ReactElement {
  return (
    <div>
      <label htmlFor={id} className="text-xs font-medium text-muted-foreground block mb-1">
        {label}
      </label>
      <input
        id={id}
        type="number"
        step="any"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
    </div>
  );
}

function GstCard(): React.ReactElement {
  const { data, isLoading, error } = useGlobalSettings();
  const updateMutation = useUpdateGlobalSettings();
  const [gstEnabled, setGstEnabled] = useState(false);

  useEffect(() => {
    if (data) setGstEnabled(data.gstEnabled);
  }, [data]);

  const handleToggle = (checked: boolean): void => {
    setGstEnabled(checked);
    updateMutation.mutate(
      { gstEnabled: checked },
      {
        onSuccess: () => toast.success("GST setting saved"),
        onError: () => {
          setGstEnabled(!checked);
          toast.error("Failed to save GST setting");
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">GST</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground" data-testid="gst-setting-loading">
            Loading…
          </p>
        ) : error ? (
          <p className="text-sm text-destructive" data-testid="gst-setting-error">
            Failed to load GST setting.
          </p>
        ) : (
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={gstEnabled}
              onChange={(e) => handleToggle(e.target.checked)}
              disabled={updateMutation.isPending}
              className="h-4 w-4"
              data-testid="gst-enabled-toggle"
            />
            Enable GST system-wide
          </label>
        )}
      </CardContent>
    </Card>
  );
}

function ReferencePrefixCard(): React.ReactElement {
  const { data, isLoading, error } = useReferencePrefix();
  const updateMutation = useUpdateReferencePrefix();
  const [prefix, setPrefix] = useState("GP:");

  useEffect(() => {
    if (data) setPrefix(data.referencePrefix);
  }, [data]);

  const handleSave = (): void => {
    updateMutation.mutate(
      { referencePrefix: prefix },
      {
        onSuccess: () => toast.success("Reference prefix saved"),
        onError: () => toast.error("Failed to save reference prefix"),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-semibold">Reference Prefix</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground" data-testid="reference-prefix-loading">
            Loading…
          </p>
        ) : error ? (
          <p className="text-sm text-destructive" data-testid="reference-prefix-error">
            Failed to load reference prefix.
          </p>
        ) : (
          <div className="flex items-end gap-2">
            <div className="flex-1 max-w-[200px]">
              <label htmlFor="reference-prefix" className="text-xs font-medium text-muted-foreground block mb-1">
                Prefix used on cover letter references (e.g. GP: 0926XXXX)
              </label>
              <input
                id="reference-prefix"
                type="text"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>
            <Button onClick={handleSave} disabled={updateMutation.isPending} data-testid="save-reference-prefix">
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function GlobalDefaultsPage(): React.ReactElement {
  const [localOnly, setLocalOnly] = useState(initialLocalOnlyDefaults);
  const updateLocalOnly = (field: keyof LocalOnlyDefaults, value: number) =>
    setLocalOnly((d) => ({ ...d, [field]: value }));

  const { data: inflationData } = useInflationDefaults();
  const updateInflationMutation = useUpdateInflationDefaults();
  const [inflation, setInflation] = useState(initialInflationDefaults);
  const updateInflation = (field: keyof InflationDefaults, value: number) =>
    setInflation((d) => ({ ...d, [field]: value }));

  useEffect(() => {
    if (inflationData) setInflation(inflationData);
  }, [inflationData]);

  const handleSave = () => {
    updateInflationMutation.mutate(inflation, {
      onSuccess: () => toast.success("Global defaults saved"),
      onError: () => toast.error("Failed to save global defaults"),
    });
  };

  return (
    <div>
      <h2 className="text-lg font-semibold mb-1">Global Defaults</h2>
      <p className="text-sm text-muted-foreground mb-1">
        These values pre-populate new insurance records. Changing them does not affect existing records.
      </p>
      <p className="text-xs text-muted-foreground mb-3">Last updated: — by —</p>

      <div className="space-y-4 max-w-2xl">
        <GstCard />
        <ReferencePrefixCard />

        <div>
          <h3 className="text-sm font-semibold mb-3">Financial Defaults</h3>
          <div className="grid grid-cols-3 gap-4">
            <Field
              label="Fees Percentage"
              id="d-fees"
              value={localOnly.feesPercentage}
              onChange={(v) => updateLocalOnly("feesPercentage", v)}
            />
            <Field
              label="Demolition Percentage"
              id="d-demo"
              value={localOnly.demolitionPercentage}
              onChange={(v) => updateLocalOnly("demolitionPercentage", v)}
            />
            <Field
              label="GST Rate"
              id="d-gst"
              value={localOnly.gstRate}
              onChange={(v) => updateLocalOnly("gstRate", v)}
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Inflation Metric Defaults</h3>
          <div className="grid grid-cols-3 gap-4">
            <Field
              label="Demo/Plan Months"
              id="d-dpm"
              value={inflation.demoPlanMonths}
              onChange={(v) => updateInflation("demoPlanMonths", v)}
            />
            <Field
              label="Construction Months"
              id="d-cm"
              value={inflation.constructionMonths}
              onChange={(v) => updateInflation("constructionMonths", v)}
            />
            <Field
              label="First Year Months"
              id="d-fym"
              value={inflation.firstYearMonths}
              onChange={(v) => updateInflation("firstYearMonths", v)}
            />
            <Field
              label="Inflation Year 1 %"
              id="d-y1"
              value={inflation.inflationYear1}
              onChange={(v) => updateInflation("inflationYear1", v)}
            />
            <Field
              label="Inflation Year 2 %"
              id="d-y2"
              value={inflation.inflationYear2}
              onChange={(v) => updateInflation("inflationYear2", v)}
            />
            <Field
              label="Inflation Year 3 %"
              id="d-y3"
              value={inflation.inflationYear3}
              onChange={(v) => updateInflation("inflationYear3", v)}
            />
            <Field
              label="Inflation Year 4 %"
              id="d-y4"
              value={inflation.inflationYear4}
              onChange={(v) => updateInflation("inflationYear4", v)}
            />
          </div>
        </div>

        <div>
          <h3 className="text-sm font-semibold mb-3">Modal Defaults</h3>
          <div className="grid grid-cols-2 gap-4">
            <Field label="S Modal" id="d-sm" value={localOnly.sModal} onChange={(v) => updateLocalOnly("sModal", v)} />
            <Field label="E Modal" id="d-em" value={localOnly.eModal} onChange={(v) => updateLocalOnly("eModal", v)} />
          </div>
        </div>

        <Button onClick={handleSave} data-testid="save-defaults">
          <Save className="h-3.5 w-3.5" /> Save Defaults
        </Button>
      </div>
    </div>
  );
}
